/* eslint-disable react/no-array-index-key */
import {SerializedStyles} from '@emotion/react';
import {Autocomplete, css, AutocompleteProps, TextField, useTheme, Typography} from '@mui/material';
import {isEmpty} from 'lodash';
import React, {useMemo} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {DlSelectSelectedValue, GetStylesFunction} from 'src/js/common/types';
import {getValue, isOptionEqualToValue} from 'src/js/common/utils/input_utils';
import TranslateUtils from 'src/js/common/utils/translate_utils';

import {DlMuiCaretDownIcon} from './DlMuiCaretDownIcon';
import {DlMuiChip} from './DlMuiChip';
import {DlMuiDeleteIcon} from './DlMuiDeleteIcon';
import {StringUtils} from '../common';

const I18N = defineMessages({
  LOADING: {
    id: 'hierarchy.children.loading',
    defaultMessage: 'Loading ...',
    description: 'Dropdown label when options are loading',
  },
  NO_OPTIONS: {
    id: 'filter.dropDown.noOptions',
    defaultMessage: 'No matches found',
    description: 'Dropdown label when no options are available',
  },
  PLACEHOLDER: {
    id: 'filter.dropDown.placeholder',
    defaultMessage: 'Select...',
    description: 'placeholder in drop down input',
  },
});

export interface DlMuiAutocompleteOwnProps extends AutocompleteProps<any, any, any, any> {
  inputCss?: SerializedStyles;
  inputRef: React.Ref<HTMLElement | null>;
  isClearable: boolean;
  isMulti: boolean;
  label: string;
  labelProperty?: string;
  loadingText?: string;
  noOptionsText?: string;
  onChange: (value: any) => void;
  placeholderText: string;
  selectedValue: DlSelectSelectedValue | undefined;
  valueProperty: string;
  error?: boolean;
  helperText?: string;
  isDefaultValue?: boolean;
  getOptionDisabled?: (row: any) => boolean;
  maxWidth?: string | number;
}

type Props = DlMuiAutocompleteOwnProps;

export const DlMuiAutocomplete: React.FC<Props> = (props) => {
  const {
    isMulti,
    inputRef,
    selectedValue,
    valueProperty,
    labelProperty,
    label,
    loadingText = I18N.LOADING.id,
    noOptionsText = I18N.NO_OPTIONS.id,
    onChange,
    options,
    placeholderText = I18N.PLACEHOLDER.id,
    inputCss,
    isClearable,
    isDefaultValue,
    error,
    helperText,
    getOptionDisabled,
    maxWidth,
    disabled,
    ...restProps
  } = props;

  const value = useMemo(
    // null is not supported, converting null to undefined if necessary
    () => getValue(selectedValue, options, isMulti, valueProperty) ?? undefined,
    [isMulti, options, selectedValue, valueProperty]
  );
  const theme = useTheme();
  let isOccupied = !isDefaultValue;
  if (isDefaultValue === undefined) isOccupied = !isEmpty(value);
  const muiStyles = getStyles({theme, params: {inputCss, maxWidth}});
  const intl = useIntl();

  const dynamicProps = {
    ...(labelProperty ? {getOptionLabel: (option) => String(option[labelProperty])} : {}),
  };

  const handleChange = (event: any, newValue: any) => {
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <div css={muiStyles.autocompleteWrapper}>
      <Autocomplete
        data-name={'dl-mui-autocomplete--autocomplete'}
        disabled={disabled}
        popupIcon={<DlMuiCaretDownIcon />}
        multiple={isMulti}
        ref={inputRef}
        disableClearable={!isClearable}
        noOptionsText={TranslateUtils.translate(noOptionsText, intl)}
        loadingText={TranslateUtils.translate(loadingText, intl)}
        isOptionEqualToValue={(option, value) => {
          return isOptionEqualToValue(option, value, valueProperty);
        }}
        css={muiStyles.autocomplete}
        options={options}
        {...dynamicProps}
        {...restProps}
        renderInput={(params) => (
          <TextField
            {...params}
            label={TranslateUtils.translate(label, intl)}
            placeholder={TranslateUtils.translate(placeholderText, intl)}
            css={muiStyles.textField}
            error={error}
            helperText={helperText}
            data-occupied={isOccupied}
            data-disabled={Boolean(disabled)}
            data-error={Boolean(error?.bool)}
          />
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <DlMuiChip
              {...getTagProps({index})}
              key={`${option.id}-${index}`}
              label={option.label}
              deleteIcon={isMulti ? <DlMuiDeleteIcon /> : undefined}
            />
          ))
        }
        renderOption={(props, option, rowInfo) => {
          const {key} = props as any;
          const {label} = option;
          return (
            <Typography {...props} key={`${key}-${rowInfo?.index}`} variant={'value'} css={muiStyles.autocompleteOption}>
              {label}
            </Typography>
          );
        }}
        onChange={handleChange}
        value={isMulti ? value : value?.[0] || null} // NOTE: earlier, we turn value into [value] when multi=false
        getOptionDisabled={getOptionDisabled}
      />
    </div>
  );
};

DlMuiAutocomplete.displayName = 'DlMuiAutocomplete';

const getStyles: GetStylesFunction = (options) => {
  const maxWidth = options?.params?.maxWidth;
  return {
    autocompleteWrapper: css`
      display: flex;
      max-width: ${StringUtils.addPx(maxWidth === undefined ? 362 : maxWidth)};
      width: 100%;
    `,
    autocomplete: css(
      {
        '& .MuiInputLabel-root': {
          zIndex: 11,
        },
        'div.MuiAutocomplete-tag': {
          maxWidth: '175px',
        },
      },
      options?.params?.inputCss
    ),
    textField: css({}),
    autocompleteOption: css({
      'color': 'var(--dlThemeGray-800)',

      '&:active, &:visited, &:hover, &:focus': {
        backgroundColor: options?.theme?.palette.custom.canvasGray as string,
        color: 'var(--dlThemeGray-800)',
      },

      '&:active:focus, &:active:hover': {
        backgroundColor: 'var(--dlTheme-600)',
        color: options?.theme?.palette.custom.white as string,
        outline: 'none',
      },

      '&.MuiAutocomplete-option[aria-selected="true"]': {
        'backgroundColor': 'var(--dlTheme-500)',
        'color': options?.theme?.palette.custom.white as string,

        '&.MuiAutocomplete-option[aria-selected="true"]:hover': {
          backgroundColor: 'var(--dlTheme-600)',
          color: options?.theme?.palette.custom.white as string,
        },
        '&.MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
          backgroundColor: 'var(--dlTheme-600)',
          color: options?.theme?.palette.custom.white as string,
        },
      },
      '&.MuiAutocomplete-option[aria-disabled="true"]': {
        opacity: 1,
        background: options?.theme?.palette.custom.dlHierarchyNodeItemDisabledBackground as string,
        color: options?.theme?.palette.custom.dlHierarchyNodeItemDisabled as string,
      },
    }),
  };
};
